/**
 * ONE TAP — REST helpers for the room lifecycle.
 *
 * Everything here talks to the Worker over *relative* URLs so the exact same
 * code path works behind the Vite dev proxy (`/api` -> wrangler dev on :8787)
 * and in production where the Worker serves the static bundle itself.
 *
 * Endpoints (see src/worker/):
 *   POST /api/room/new           -> { code: "ONETAP-XXXX" }
 *   GET  /api/room/:code/exists  -> { exists: boolean }
 *   WS   /api/room/:code/ws      -> the GameRoom Durable Object socket
 */

import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, ROOM_CODE_PREFIX } from '@shared/constants';

/** How long a REST call is allowed to hang before we give up and surface an error. */
const REQUEST_TIMEOUT_MS = 8000;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Room codes
// ---------------------------------------------------------------------------

/**
 * Canonicalise anything a human might type or paste into `ONETAP-XXXX`.
 * Accepts "abcd", "ONETAP-ABCD", "onetap abcd", or a pasted invite link.
 *
 * The code alphabet already excludes the ambiguous glyphs (O/0, I/1, S/5), so
 * the only folding we do is the pair where the intent is unambiguous: a typed
 * `0` or `O` can only have meant `Q`, the one round glyph the alphabet keeps.
 * Anything else outside the alphabet is dropped and validation will reject it,
 * which is far better than silently joining the wrong room.
 */
export function normalizeRoomCode(input: string): string {
  let s = (input ?? '').toUpperCase().trim();

  // Tolerate a pasted link or hash fragment: pull the code-looking tail out of it.
  const urlMatch = s.match(/(?:ROOM\/|CODE=|#)([A-Z0-9-]+)/);
  if (urlMatch && urlMatch[1]) s = urlMatch[1];

  s = s.replace(/[^A-Z0-9]/g, '');
  if (s.startsWith('ONETAP')) s = s.slice('ONETAP'.length);

  let folded = '';
  for (const ch of s) {
    const c = ch === '0' || ch === 'O' ? 'Q' : ch;
    if (ROOM_CODE_ALPHABET.includes(c)) folded += c;
  }

  return ROOM_CODE_PREFIX + folded.slice(0, ROOM_CODE_LENGTH);
}

export function isValidRoomCode(code: string): boolean {
  if (!code.startsWith(ROOM_CODE_PREFIX)) return false;
  const body = code.slice(ROOM_CODE_PREFIX.length);
  if (body.length !== ROOM_CODE_LENGTH) return false;
  for (const ch of body) if (!ROOM_CODE_ALPHABET.includes(ch)) return false;
  return true;
}

/** Relative path of the room's WebSocket endpoint. Feed this straight to `new Connection(...)`. */
export function roomWsPath(code: string): string {
  return `/api/room/${encodeURIComponent(code)}/ws`;
}

/** Shareable absolute link that drops a friend straight into the join flow. */
export function roomShareUrl(code: string): string {
  if (typeof location === 'undefined') return code;
  return `${location.origin}${location.pathname}#${code}`;
}

/** Room code embedded in the page URL hash, if any. */
export function roomCodeFromLocation(): string | null {
  if (typeof location === 'undefined') return null;
  const raw = location.hash.replace(/^#/, '');
  if (!raw) return null;
  const code = normalizeRoomCode(raw);
  return isValidRoomCode(code) ? code : null;
}

// ---------------------------------------------------------------------------
// Fetch plumbing
// ---------------------------------------------------------------------------

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      signal: controller.signal,
      headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (err) {
    clearTimeout(timer);
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    throw new ApiError(
      aborted ? 'Server did not respond in time.' : 'Could not reach the server.',
      0,
      aborted ? 'timeout' : 'network',
    );
  }
  clearTimeout(timer);

  let body: unknown = null;
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const rec = (body ?? {}) as Record<string, unknown>;
    const message = typeof rec.message === 'string' ? rec.message : `Request failed (${res.status}).`;
    const code = typeof rec.code === 'string' ? rec.code : `http_${res.status}`;
    throw new ApiError(message, res.status, code);
  }

  return (body ?? {}) as T;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** Ask the Worker to mint a fresh room. Resolves to a canonical `ONETAP-XXXX` code. */
export async function createRoom(): Promise<string> {
  const body = await request<{ code?: string }>('/api/room/new', { method: 'POST' });
  const code = typeof body.code === 'string' ? normalizeRoomCode(body.code) : '';
  if (!isValidRoomCode(code)) {
    throw new ApiError('Server returned an unusable room code.', 500, 'bad_code');
  }
  return code;
}

/**
 * Does this room exist and still accept joins? Used to fail fast in the menu
 * instead of opening a socket that immediately closes.
 */
export async function roomExists(code: string): Promise<boolean> {
  const normalized = normalizeRoomCode(code);
  if (!isValidRoomCode(normalized)) return false;
  try {
    const body = await request<{ exists?: boolean }>(`/api/room/${encodeURIComponent(normalized)}/exists`);
    return body.exists === true;
  } catch (err) {
    // A 404 is a definitive "no such room", not a transport failure.
    if (err instanceof ApiError && err.status === 404) return false;
    throw err;
  }
}
